/**
 * WHAT THE HOUSE COMES TO, AND WHY THAT NUMBER.
 *
 * The editor has refused to show a price since it was written, and the reason
 * is in the panel it hid: a confident dollar figure derived from one reference
 * length in one plane of a photograph is exactly the thing not to put in front
 * of a customer. That reason was right, and it has stopped being true.
 *
 * The footage here is not derived from a photograph. It is what somebody
 * measured at the house, section by section, and the gate says out loud how
 * much of it is still a guess. Pricing a walked measurement is the whole point
 * of having walked it.
 *
 * NOTHING NEW IS INVENTED. The per-foot rate is the account's own
 * rates.roofPerFoot, and the discount is the existing seasonal ladder with its
 * existing percentages. This file multiplies and subtracts; it does not decide
 * what anything is worth.
 *
 * MONEY IS INTEGER CENTS, rounded once at the multiplication, the same rule
 * the quote document has always used. A price that disagrees with the printed
 * sheet by a cent is a price nobody trusts twice.
 */

import { TIERS, tierDef, type SeasonTier } from '../types/pricing'
import type { RoofSection } from './sections'
import { sectionTotals } from './sections'

export interface Estimate {
  /** Feet across included sections. The thing being sold. */
  feet: number
  /** Of those, how many nobody has measured at the house. */
  unverifiedFeet: number
  /** Dollars per installed foot, from the account's rate card. */
  perFoot: number
  /** feet x perFoot, rounded once. */
  subtotal: number
  tier: SeasonTier
  tierLabel: string
  discountPct: number
  /** A positive number. The line reads as a subtraction. */
  discount: number
  total: number
  /**
   * False when nobody has set a rate. A price of zero is not a price, and a
   * price built on the placeholder 1 is worse: it looks like a decision.
   */
  priced: boolean
  /** True when every included foot was measured at the house. */
  solid: boolean
}

const cents = (n: number) => Math.round(n * 100)
const money = (c: number) => c / 100

/**
 * The per-foot rate the account actually carries. The shipped default is 1,
 * which is a placeholder rather than a dollar, so it is treated as unset:
 * a house priced at one dollar a foot is not a business.
 */
export const rateIsSet = (perFoot: number): boolean => perFoot > 1

export function estimate(
  sections: RoofSection[],
  perFoot: number,
  tier: SeasonTier,
): Estimate {
  const t = sectionTotals(sections)
  const def = tierDef(tier)
  const priced = rateIsSet(perFoot)

  // Rounded once, at the multiplication, exactly as a quote line is.
  const subC = priced ? cents(t.feet * perFoot) : 0
  const discC = Math.round((subC * def.discountPct) / 100)

  return {
    feet: t.feet,
    unverifiedFeet: t.unverifiedFeet,
    perFoot,
    subtotal: money(subC),
    tier,
    tierLabel: def.label,
    discountPct: def.discountPct,
    discount: money(discC),
    total: money(subC - discC),
    priced,
    solid: t.allVerified,
  }
}

/**
 * A PACKAGE IS WHICH SECTIONS ARE IN IT.
 *
 * The prototype's two packages are the same house at two scopes: the garage
 * and the entry gable, or those plus the right eave. Not two products - the
 * same C9 line, more of the house lit. That is what a homeowner is actually
 * choosing between on a driveway, and it maps onto the exclusion each section
 * already carries rather than needing a second idea.
 */
export interface ScopePackage {
  id: string
  name: string
  /** What it covers, in the customer's terms. */
  covers: string
  /** Section ids in this package. */
  sectionIds: string[]
  estimate: Estimate
}

/**
 * Two packages off one house: everything measured, and everything minus the
 * single longest section. Two, because the workflow document asks for two or
 * three meaningful ones rather than a wall of similar cards, and because the
 * choice a homeowner makes at the door is how much of the house to light.
 *
 * Returns an empty list when there is nothing to split - one section is not a
 * choice between two scopes, it is one scope.
 */
export function scopePackages(
  sections: RoofSection[],
  perFoot: number,
  tier: SeasonTier,
): ScopePackage[] {
  const included = sections.filter((s) => !s.excluded && s.measurement.feet > 0)
  if (included.length < 2) return []

  const longest = included.reduce((a, b) =>
    b.measurement.feet > a.measurement.feet ? b : a,
  )
  const lesser = included.filter((s) => s.id !== longest.id)

  const name = (list: RoofSection[]) =>
    list
      .map((s) => s.label.trim() || 'a section')
      .slice(0, 3)
      .join(', ')

  return [
    {
      id: 'essential',
      name: 'Essential',
      covers: name(lesser),
      sectionIds: lesser.map((s) => s.id),
      estimate: estimate(lesser, perFoot, tier),
    },
    {
      id: 'complete',
      /*
       * SIGNATURE, NOT COMPLETE. The card above this name carries the eyebrow
       * "Complete front", so the two read as "Complete front / Complete" -
       * the coverage said twice with the tier missing. The design package
       * names the two tiers Essential and Signature, and the eyebrow is the
       * coverage. Those are two different facts and now they read as two.
       */
      name: 'Signature',

      covers: name(included),
      sectionIds: included.map((s) => s.id),
      estimate: estimate(included, perFoot, tier),
    },
  ]
}

/** The three tiers, so a screen can show the cost of waiting. */
export const SEASON_TIERS = TIERS
